/**
 * Lazy-loaded Stripe.js. Under the WooPay flow we initialise Stripe with
 * `stripeAccount: acct_…` so all `createPaymentMethod` / `handleNextAction`
 * calls hit the WooPayments-managed Stripe Express account that owns this
 * merchant's card processing — never a standalone Stripe account (we don't
 * have one).
 *
 * Both values are public — they ship in the bundle alongside the page that
 * uses them. They come from `/api/pay/public-config`, which reads them from
 * Firestore (`settings/payments.woopay`).
 */
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { getPublicStripeConfig } from "./give-api";

let cachedPromise: Promise<Stripe | null> | null = null;
let cacheKey = "";

export async function getStripe(): Promise<Stripe | null> {
  const cfg = await getPublicStripeConfig();
  if (!cfg.publishableKey) {
    throw new Error(
      "Stripe publishable key not configured. WooPayments must be onboarded before card payment is available.",
    );
  }
  const key = `${cfg.publishableKey}|${cfg.accountId || ""}`;
  if (cachedPromise && cacheKey === key) return cachedPromise;
  cacheKey = key;
  cachedPromise = loadStripe(
    cfg.publishableKey,
    cfg.accountId ? { stripeAccount: cfg.accountId } : undefined,
  );
  return cachedPromise;
}
